import type {
  AgendaOutcomeKind,
  BookkeeperMeeting,
  MeetingActionItem,
  MeetingAgendaLine,
  MeetingWithItems,
  NextMeetingProposal,
  Review,
} from '../types';
import { fetchApi } from './client';

// Meetings
export async function getMeetings(params?: {
  supervisorId?: string;
  bookkeeperId?: string;
  status?: string;
}): Promise<BookkeeperMeeting[]> {
  const searchParams = new URLSearchParams();
  if (params?.supervisorId) searchParams.set('supervisorId', params.supervisorId);
  if (params?.bookkeeperId) searchParams.set('bookkeeperId', params.bookkeeperId);
  if (params?.status) searchParams.set('status', params.status);

  const query = searchParams.toString();
  return fetchApi<BookkeeperMeeting[]>(`/meetings${query ? `?${query}` : ''}`);
}

// NOTE: the route is the sibling-literal `/meetings-upcoming`, not `/meetings/upcoming` — the
// latter is shadowed by `/meetings/{id}` (same GET verb) at the Functions host, which served it as
// GetMeeting(id: "upcoming") and answered 404 "Meeting not found". That silently emptied the
// Upcoming box on the Meetings page. Same gotcha as `/action-items-batch` below; see
// reference_functions_route_shadowing. Backend route pinned by MeetingFunctionsTests.
export async function getUpcomingMeetings(daysAhead: number = 7): Promise<BookkeeperMeeting[]> {
  return fetchApi<BookkeeperMeeting[]>(`/meetings-upcoming?days=${daysAhead}`);
}

export async function getMeeting(id: string): Promise<MeetingWithItems> {
  // The backend nests the meeting under a `meeting` key alongside the related arrays:
  //   { meeting, carryoverItems, newItems, previousMeetingItems, previousMeetingDate }
  // MeetingWithItems is a flat shape (extends BookkeeperMeeting), so spread the meeting
  // fields up to the top level and attach the arrays. Without this the Active Meeting page
  // reads every meeting field as undefined (blank header, Invalid Date, dead Start/Complete).
  const data = await fetchApi<{
    meeting: BookkeeperMeeting;
    carryoverItems?: MeetingActionItem[];
    newItems?: MeetingActionItem[];
    reviewsToDiscuss?: Review[];
    previousMeetingItems?: MeetingActionItem[];
    previousMeetingDate?: string;
  }>(`/meetings/${id}`);
  return {
    ...data.meeting,
    carryoverItems: data.carryoverItems ?? [],
    newItems: data.newItems ?? [],
    reviewsToDiscuss: data.reviewsToDiscuss ?? [],
    previousMeetingItems: data.previousMeetingItems ?? [],
    previousMeetingDate: data.previousMeetingDate,
  };
}

export async function createMeeting(data: {
  supervisorId: string;
  supervisorName: string;
  bookkeeperId: string;
  bookkeeperName: string;
  scheduledDate: string;
  scheduledTime: string;
}): Promise<BookkeeperMeeting> {
  return fetchApi<BookkeeperMeeting>('/meetings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMeeting(
  id: string,
  data: Partial<BookkeeperMeeting>
): Promise<BookkeeperMeeting> {
  return fetchApi<BookkeeperMeeting>(`/meetings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function startMeeting(id: string): Promise<BookkeeperMeeting> {
  return fetchApi<BookkeeperMeeting>(`/meetings/${id}/start`, {
    method: 'POST',
  });
}

export async function completeMeeting(id: string): Promise<BookkeeperMeeting> {
  return fetchApi<BookkeeperMeeting>(`/meetings/${id}/complete`, {
    method: 'POST',
  });
}

// The agenda (docs/specs/meetings-agenda.md). The preview route is the hyphen-folded literal, never
// `/meetings/agenda-preview`, which GET `/meetings/{id}` would swallow (the same trap as /meetings-upcoming).
export async function getAgendaPreview(bookkeeperId: string): Promise<MeetingAgendaLine[]> {
  return fetchApi<MeetingAgendaLine[]>(`/meetings-agenda-preview?bookkeeperId=${encodeURIComponent(bookkeeperId)}`);
}

export async function recordAgendaOutcome(
  meetingId: string,
  lineId: string,
  outcome: { kind: AgendaOutcomeKind; dueDate?: string; actionItemId?: string; actionItemTitle?: string },
): Promise<MeetingAgendaLine> {
  return fetchApi<MeetingAgendaLine>(`/meetings/${meetingId}/agenda/${lineId}/outcome`, {
    method: 'POST',
    body: JSON.stringify(outcome),
  });
}

export async function getNextMeetingProposal(meetingId: string): Promise<NextMeetingProposal> {
  return fetchApi<NextMeetingProposal>(`/meetings/${meetingId}/next-proposal`);
}

export async function setActionItemMeetingFlag(id: string, flagged: boolean): Promise<MeetingActionItem> {
  return fetchApi<MeetingActionItem>(`/action-items/${id}/meeting-flag`, {
    method: 'POST',
    body: JSON.stringify({ flagged }),
  });
}

export async function setReviewMeetingFlag(id: string, flagged: boolean): Promise<unknown> {
  return fetchApi(`/client-reviews/${id}/meeting-flag`, {
    method: 'POST',
    body: JSON.stringify({ flagged }),
  });
}

// Action Items
export async function getActionItems(params?: {
  bookkeeperId?: string;
  status?: string;
  supervisorId?: string;
}): Promise<{ items: MeetingActionItem[]; totalCount: number; openCount: number; overdueCount: number }> {
  const searchParams = new URLSearchParams();
  if (params?.bookkeeperId) searchParams.set('bookkeeperId', params.bookkeeperId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.supervisorId) searchParams.set('supervisorId', params.supervisorId);

  const query = searchParams.toString();
  return fetchApi(`/action-items${query ? `?${query}` : ''}`);
}

export async function getOpenItemsForBookkeeper(bookkeeperId: string): Promise<MeetingActionItem[]> {
  return fetchApi<MeetingActionItem[]>(`/action-items/open/${bookkeeperId}`);
}

export async function createActionItem(data: {
  sourceType: string;
  sourceReviewId?: string;
  sourceClientId?: string;
  sourceClientName?: string;
  bookkeeperId: string;
  bookkeeperName: string;
  supervisorId: string;
  supervisorName: string;
  title: string;
  description?: string;
  category: string;
  priority: string;
  dueDate?: string;
  createdInMeetingId: string;
}): Promise<MeetingActionItem> {
  return fetchApi<MeetingActionItem>('/action-items', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateActionItem(
  id: string,
  data: {
    title?: string;
    category?: string;
    status?: string;
    priority?: string;
    dueDate?: string;
    description?: string;
    blockedReason?: string;
    resolutionNotes?: string;
    changedInMeetingId?: string;
  }
): Promise<MeetingActionItem> {
  return fetchApi<MeetingActionItem>(`/action-items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function completeActionItem(
  id: string,
  data?: { resolutionNotes?: string; changedInMeetingId?: string }
): Promise<MeetingActionItem> {
  return fetchApi<MeetingActionItem>(`/action-items/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function carryForwardActionItem(
  id: string,
  meetingId?: string
): Promise<MeetingActionItem> {
  return fetchApi<MeetingActionItem>(`/action-items/${id}/carry-forward`, {
    method: 'POST',
    body: JSON.stringify({ changedInMeetingId: meetingId }),
  });
}

export interface BatchUpdateResult {
  updated: number;
  unauthorizedIds: string[];
  notFoundIds: string[];
}

// Bulk status change. NOTE: the route is the sibling-literal `/action-items-batch`, not
// `/action-items/batch` — the latter is shadowed by `/action-items/{id}` (same PATCH verb) at the
// Functions host (see reference_functions_route_shadowing). The backend fans out a single
// `actionItem.batchUpdated` SignalR event so the client invalidates ['action-items'] once.
export async function batchUpdateActionItems(
  ids: string[],
  status: string,
  resolutionNotes?: string
): Promise<BatchUpdateResult> {
  return fetchApi<BatchUpdateResult>(`/action-items-batch`, {
    method: 'PATCH',
    body: JSON.stringify({ ids, status, resolutionNotes }),
  });
}
